import { useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { ScrollTop } from '@captaincause/ui/ScrollTop'
import { getProjects, getProjectsFilters } from 'api/projects'
import { NavAndFooterLayout } from 'layouts/NavAndFooter'
import { ProjectFiltersBar } from 'components/Project/Filtersbar'
import { PageHead } from 'pages/Head'
import { getBanner } from 'pages/api/banner'
import { injectI18n } from 'utils/i18n'
import locales from 'i18n/locales'
import { ProjectsList } from './List'
import { Hero } from './Hero'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import type { NextPageWithLayout } from 'app'
import type { Locales, Project, ProjectFilters } from '@captaincause/types'
import type { ProjectFiltersBarImperativeRef } from 'components/Project/Filtersbar'

interface PageProps {
  projects: Project[]
  filters: ProjectFilters | null
}

const LOCALIZATIONS = ['fr', 'en']

export const getStaticProps: GetStaticProps<PageProps> = async context => {
  if (!locales.SUPPORTED_LOCALES.includes(context.locale as string)) {
    return {
      notFound: true,
    }
  }

  const i18n = await injectI18n(context.locale, [
    'filters.component',
    'footer.component',
    'metadata',
    'nav.component',
    'projects.page',
  ])

  const banner = await getBanner({ locale: 'fr' })

  try {
    const projects = await getProjects({ locale: context.locale as Locales })
    const filters = await getProjectsFilters({
      locale: context.locale as Locales,
    })

    const filteredThemes = filters.themes.filter(theme =>
      projects.some(
        project =>
          project.theme1?.theme === theme.theme ||
          project.theme2?.theme === theme.theme,
      ),
    )

    return {
      props: {
        projects,
        filters: {
          regions: filters.regions,
          themes: filteredThemes,
        },
        banner,
        localizations: LOCALIZATIONS.map(locale => ({ locale })),
        ...i18n,
      },
      revalidate: 120,
    }
  } catch (error: unknown) {
    return {
      props: { projects: [], filters: null, ...i18n },
      revalidate: 120,
    }
  }
}

type InferredPageProps = InferGetStaticPropsType<typeof getStaticProps>
const Projects: NextPageWithLayout<InferredPageProps> = ({
  projects: allProjects,
  filters,
}: InferredPageProps) => {
  const filtersBarRef = useRef<ProjectFiltersBarImperativeRef>(null)
  const [projects, setProjects] = useState<Project[]>(allProjects)

  const { t } = useTranslation('projects.page')

  return (
    <>
      <PageHead
        head={{
          title: t('metadata.title'),
          description: t('metadata.description'),
          alternates: [
            {
              locale: 'fr',
              href: 'https://dift.com/projects',
            },
            {
              locale: 'en',
              href: 'https://dift.com/en/projects',
            },
          ],
        }}
      />
      <main
        id="main"
        className="relative mx-auto w-full max-w-screen-2xl flex-1 border-b border-gray md:px-14 md:py-10"
      >
        <Hero />
        {filters ? (
          <ProjectFiltersBar
            projects={projects}
            filters={filters}
            onFilter={filteredProjects => setProjects(filteredProjects)}
            ref={filtersBarRef}
            className="px-4 md:px-0"
          />
        ) : null}
        <ProjectsList
          projects={projects}
          clearFilters={filtersBarRef?.current?.clearFilters}
        />
        <ScrollTop />
      </main>
    </>
  )
}

Projects.Layout = NavAndFooterLayout
Projects.pagePropsOnLayout = true

export default Projects
